import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, Package } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

/* Starterpaket (11.10.2026): wer hat sein Startpaket aus dem Betreuten Einstieg noch nicht bekommen?
   Die Übergabe wird am Vertrag festgehalten. Wer die Verträge nicht sehen darf, sieht die Karte nicht. */
const datum = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('.') : '–');
const ART = { kinder: 'Kind', erwachsene: 'Erwachsen', family: 'Family' };

export default function StarterpaketKarte() {
  const { user } = useAuth();
  const leitung = user?.role === 'admin' || user?.role === 'manager';
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const [alle, setAlle] = useState(false);
  const [ruecknahme, setRuecknahme] = useState(null);     // vertrag_id
  const [grund, setGrund] = useState('');
  const laden = useCallback(() => api.get('/starterpaket').then((r) => setD(r.data)).catch(() => setD(false)), []);
  useEffect(() => { laden(); }, [laden]);
  if (!d) return null;

  const erhalten = async (z) => {
    setBusy(z.vertrag_id);
    try {
      await api.post(`/starterpaket/${z.vertrag_id}/erhalten`, {});
      toast.success(`Starterpaket für ${z.name} eingetragen`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nicht eingetragen');
    }
    setBusy(null);
    laden();
  };
  const zurueck = async () => {
    if (grund.trim().length < 5) { toast.error('Bitte eine Begründung eintragen'); return; }
    try {
      await api.post(`/starterpaket/${ruecknahme}/zuruecknehmen`, { grund: grund.trim() });
      toast.success('Zurückgenommen');
      setRuecknahme(null); setGrund('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nicht zurückgenommen');
    }
    laden();
  };

  return (
    <Card className="border-border/60" data-testid="starterpaket-karte">
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2"><Package className="w-4 h-4" /> Starterpaket</CardTitle>
        <span className="text-xs text-muted-foreground">{d.pakete_offen} offen · {d.erhalten.length} ausgegeben</span>
      </CardHeader>
      <CardContent className="space-y-2">
        {d.offen.length === 0 && <p className="text-sm text-muted-foreground">Alle Starterpakete sind ausgegeben.</p>}
        {d.offen.map((z) => (
          <div key={z.vertrag_id} className="flex items-center justify-between gap-3 rounded-lg border border-border/60 px-3 py-2" data-testid={`starterpaket-offen-${z.vertragsnummer}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{z.name}{z.anzahl > 1 ? ` (${z.anzahl} Pakete)` : ''}</div>
              <div className="text-xs text-muted-foreground">{ART[z.kategorie] || z.kategorie} · {z.vertragsnummer} · Beginn {datum(z.vertragsbeginn)}</div>
            </div>
            <Button size="sm" disabled={busy === z.vertrag_id} onClick={() => erhalten(z)}><Check className="w-4 h-4 mr-1" /> Erhalten</Button>
          </div>
        ))}
        {d.erhalten.length > 0 && (
          <button type="button" className="text-xs text-muted-foreground underline" onClick={() => setAlle(!alle)}>
            {alle ? 'Ausgegebene ausblenden' : `Ausgegebene anzeigen (${d.erhalten.length})`}
          </button>
        )}
        {alle && d.erhalten.map((z) => (
          <div key={z.vertrag_id} className="text-xs text-muted-foreground px-1 space-y-1">
            <div className="flex items-center justify-between gap-3">
              <span className="truncate"><Check className="inline w-3 h-3 text-emerald-600 mr-1" />{z.name} · {datum(z.erhalten.am)}{z.erhalten.durch ? ` · ${z.erhalten.durch}` : ''}</span>
              {leitung && <button type="button" className="underline shrink-0" onClick={() => { setRuecknahme(ruecknahme === z.vertrag_id ? null : z.vertrag_id); setGrund(''); }}>zurücknehmen</button>}
            </div>
            {ruecknahme === z.vertrag_id && (
              <div className="flex gap-2">
                <Input value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="Begründung" className="h-8 text-xs" />
                <Button size="sm" variant="outline" onClick={zurueck}>Bestätigen</Button>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
